import './App.css';
import React, { useState, useEffect } from "react";
import Hero from './components/Hero';
import ProfileBoot, { hasBootedThisSession, prefersReducedMotion } from './components/ProfileBoot';
import ScrollReveal from "./components/ScrollReveal";
import RolesSection from "./components/RolesSection";
import WorkHistory from "./components/WorkHistory";
import ProductsSection from "./components/ProductsSection";
import SystemThinking from "./components/SystemThinking";
import ToolsSection from "./components/ToolsSection";
import AboutSection from "./components/AboutSection";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Spectrum from "./Pages/Spectrum";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import NsaBrands from "./Pages/NsaBrands";
import Att from "./Pages/Att";
import Verizon from "./Pages/Verizon";
import Caterpillar from "./Pages/Caterpillar";
import Tilt from "./Pages/Tilt";
import HowIBuild from "./Pages/HowIBuild";
import LeadingEngineers from "./Pages/LeadingEngineers";
import Contact from "./Pages/Contact";
import Components from "./Pages/Components";
import SystemDesign from "./Pages/SystemDesign";
import CartSense from "./Pages/CartSense";
import FigmaToCode from "./Pages/FigmaToCode";
import Nebo from "./Pages/Nebo";
import NotFound from "./Pages/NotFound";


import { ThemeProvider, createGlobalStyle } from "styled-components";
import { lightTheme, darkTheme } from "./theme";
import { typography } from "./styles/designSystem";
import CRTOverlay from "./components/CRTOverlay";



const GlobalStyle = createGlobalStyle`
    * {
        box-sizing: border-box;
    }

    html, body {
        margin: 0;
        padding: 0;
        width: 100%;
    }

    body {
        background-color: ${({ theme }) => theme.background};
        color: ${({ theme }) => theme.text};
        font-family: ${typography.fontFamily.primary};
        font-size: 1rem;
        letter-spacing: ${typography.letterSpacing.normal};
        /* Every glyph on the screen blooms slightly, the way phosphor does. */
        text-shadow: 0 0 1px ${({ theme }) => theme.glowSoft}, 0 0 6px ${({ theme }) => theme.glowSoft};
        transition: background-color 0.3s ease, color 0.3s ease;
    }

    /* The terminal chassis behind the screen — a faint dot grid under everything. */
    body::before {
        content: "";
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 0;
        background-image:
            linear-gradient(${({ theme }) => theme.borderLight} 1px, transparent 1px),
            linear-gradient(90deg, ${({ theme }) => theme.borderLight} 1px, transparent 1px);
        background-size: 44px 44px;
        opacity: 0.25;
    }

    #root {
        width: 100%;
        overflow-x: hidden;
        position: relative;
        z-index: 1;
    }

    /* A character-grid terminal has no rounded corners. Enforced globally rather
       than file-by-file: ~26 components set their own radius, and one rounded card
       is enough to break the illusion. Scoped to #root so the Cal.com booking popup,
       which mounts on <body>, keeps its own styling. */
    #root *,
    #root *::before,
    #root *::after {
        border-radius: 0 !important;
    }

    h1, h2, h3, h4, h5, h6 {
        font-family: ${typography.fontFamily.display};
        letter-spacing: ${typography.letterSpacing.wide};
        text-transform: uppercase;
    }

    a {
        text-decoration: none;
        color: inherit;
    }

    a:visited {
        color: inherit;
    }

    a:hover {
        color: ${({ theme }) => theme.primary};
        text-shadow: 0 0 6px ${({ theme }) => theme.glow};
    }

    ::selection {
        background: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.background};
        text-shadow: none;
    }

    :focus-visible {
        outline: 2px solid ${({ theme }) => theme.primary};
        outline-offset: 2px;
    }

    /* Terminal scrollbar — a hard-edged phosphor track, no rounding anywhere. */
    * {
        scrollbar-color: ${({ theme }) => theme.primary} ${({ theme }) => theme.background};
        scrollbar-width: thin;
    }

    ::-webkit-scrollbar {
        width: 10px;
        height: 10px;
    }

    ::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.background};
        border-left: 1px solid ${({ theme }) => theme.borderLight};
    }

    ::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.phosphorDim};
        border: 2px solid ${({ theme }) => theme.background};

        &:hover {
            background: ${({ theme }) => theme.primary};
        }
    }

    img {
        /* Photographs are foreign objects on a monochrome CRT — pull them toward
           the phosphor so they sit inside the illusion instead of on top of it. */
        filter: saturate(0.55) contrast(1.05);
    }
`;


function App() {
    const [darkMode, setDarkMode] = useState(true);

    // The profile-load intro plays once per session, and never for visitors who
    // asked for reduced motion. Resolved during the first render so the terminal
    // never flashes on screen for someone who is about to skip it anyway.
    const [booting, setBooting] = useState(
        () => !hasBootedThisSession() && !prefersReducedMotion()
    );

    useEffect(() => {
        const saved = localStorage.getItem("darkMode");
        if (saved !== null) {
            setDarkMode(saved === "true");
        }
    }, []);

    const toggleDarkMode = () => {
        setDarkMode((prev) => {
            localStorage.setItem("darkMode", !prev);
            return !prev;
        });
    };

    return (
        <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
            <GlobalStyle />
            <CRTOverlay />
            {booting && <ProfileBoot onComplete={() => setBooting(false)} />}
            <Router>
                <ScrollToTop />
                <Routes>
                    <Route
                        path="/"
                        element={
                            <Layout
                                darkMode={darkMode}
                                toggleDarkMode={toggleDarkMode}
                                hideChrome={booting}
                            >
                                <Hero />
                                <ScrollReveal><RolesSection /></ScrollReveal>
                                <ScrollReveal><WorkHistory /></ScrollReveal>
                                <ScrollReveal><ProductsSection /></ScrollReveal>
                                <ScrollReveal><SystemThinking /></ScrollReveal>
                                <ScrollReveal><ToolsSection /></ScrollReveal>
                                <ScrollReveal><AboutSection /></ScrollReveal>
                            </Layout>
                        }
                    />

                    <Route
                        path="/system-design"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <SystemDesign />
                            </Layout>
                        }
                    />

                    <Route
                        path="/portfolio/cartsense"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <CartSense />
                            </Layout>
                        }
                    />

                    <Route
                        path="/portfolio/figma-to-code"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <FigmaToCode />
                            </Layout>
                        }
                    />

                    <Route
                        path="/portfolio/nebo"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Nebo />
                            </Layout>
                        }
                    />

                    <Route
                        path="/work/att"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Att />
                            </Layout>
                        }
                    />

                    <Route
                        path="/work/spectrum"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Spectrum />
                            </Layout>
                        }
                    />

                    <Route
                        path="/work/NsaBrands"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <NsaBrands />
                            </Layout>
                        }
                    />

                    <Route
                        path="/work/Verizon"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Verizon />
                            </Layout>
                        }
                    />

                    <Route
                        path="/work/Caterpillar"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Caterpillar />
                            </Layout>
                        }
                    />

                    <Route
                        path="/work/tilt"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Tilt />
                            </Layout>
                        }
                    />

                    <Route
                        path="/how-i-build"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <HowIBuild />
                            </Layout>
                        }
                    />

                    <Route
                        path="/leading-engineers"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <LeadingEngineers />
                            </Layout>
                        }
                    />

                    <Route
                        path="/contact"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Contact />
                            </Layout>
                        }
                    />

                    <Route
                        path="/components"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <Components />
                            </Layout>
                        }
                    />


                    {/* The design-system showcase folded into the component registry;
                        keep the old address working for anything already linking to it. */}
                    <Route path="/design-system" element={<Navigate to="/components" replace />} />
                    {/* The process page was rewritten as How I Build; keep the old URL working. */}
                    <Route path="/my-process" element={<Navigate to="/how-i-build" replace />} />
                    <Route path="/leadership-approach" element={<Navigate to="/leading-engineers" replace />} />
                    <Route path="/approach/design-system-architecture" element={<Navigate to="/system-design" replace />} />

                    <Route
                        path="*"
                        element={
                            <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                                <NotFound />
                            </Layout>
                        }
                    />

                </Routes>
            </Router>
        </ThemeProvider>
    );
}

export default App;
